<template>
  <div
    id="site-header-container"
    ref="siteHeaderContainer"
    class="site-header-container"
    :class="search.active ? '' : $class(headerState)"
  >
    <header
      class="site-header"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
      @mouseleave="handleMouseLeave"
    >
      <div
        ref="siteHeader"
        class="site-header__inner"
      >
        <!-- Announcement banner -->
        <announcement-banner
          v-if="announcementSettings.enabled && announcements.length"
          :announcements="announcements"
          :background-colour="announcementSettings.backgroundColour"
          :banner-speed="announcementSettings.speed"
          :text-colour="announcementSettings.textColour"
        />

        <div class="site-header__masthead">
          <div
            class="site-header__container"
            @click="handleContainerClick"
          >
            <!-- Menu icon -->
            <button
              class="site-header__menu icon-button"
              type="button"
              :aria-label="$string('general.site_header.menu')"
              @click="toggleOverlay({
                component: 'menu-drawer',
                ignoreDismissed: true,
                namespace: 'menuDrawer',
              })"
            >
              <span
                class="site-header__hamburger hamburger"
                :class="{ [$class('active')]: getActiveOverlay === 'menuDrawer' }"
              >
                <span class="hamburger__inner" />
              </span>
            </button>

            <search-bar
              ref="searchBarDesktop"
              class="site-header__search-bar site-header__search-bar--desktop"
              :redirect="search.redirect"
              :search-query="search.query"
              @search-focus="handleSearchFocus"
              @search-input="(input) => search.query = input"
              @close-search="search.active = false"
            />

            <!-- Logo -->
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
            >
              <component
                :is="titleElement"
                id="site-header-title"
                class="site-header__logo-text visually-hidden"
                v-text="$variable('store.shopName')"
              />

              <icon-childsplay />
            </a>

            <div class="site-header__main-right">
              <!-- Additional menu desktop -->
              <div class="site-header__misc-navigation">
                <ul class="site-header__navigation-list">
                  <li
                    v-for="(item) of misc.items"
                    :key="item.handle"
                    class="site-header__misc-navigation-item"
                  >
                    <a
                      class="site-header__misc-navigation-link text-heading-2xs"
                      :href="item.url"
                      v-text="item.title"
                    />
                  </li>
                </ul>
              </div>

              <!-- Account links -->
              <a
                v-if="$variable('customer.enabled')"
                class="site-header__account icon-button"
                :href="$variable('routes.account.url')"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_header.account')"
                />

                <icon-account />
              </a>

              <!-- Wishlist icon -->
              <a
                v-if="showWishlist && $variable('routes.wishlist.url')"
                class="site-header__wishlist icon-button"
                :href="$variable('routes.wishlist.url')"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_header.wishlist')"
                />

                <icon-wishlist />
              </a>

              <!-- Cart icon -->
              <a
                class="site-header__cart icon-button"
                :href="$variable('routes.cart.url')"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_header.cart')"
                />

                <icon-cart />

                <span
                  class="site-header__cart-badge"
                  aria-hidden="true"
                  v-text="cart.item_count"
                />
              </a>
            </div>
          </div>

          <!-- Search bar -->
          <search-bar
            ref="searchBarMobile"
            class="site-header__search-bar site-header__search-bar--mobile"
            :redirect="search.redirect"
            :search-query="search.query"
            @search-focus="handleSearchFocus"
            @search-input="(input) => search.query = input"
            @close-search="search.active = false"
          />
        </div>

        <!-- Navigation -->
        <nav class="site-header__navigation">
          <ul class="site-header__navigation-list">
            <li
              v-for="(tier1) of desktop.items"
              :key="tier1.handle"
              class="site-header__navigation-item"
              :class="{
                'site-header__navigation-item--active': tier1.handle === hoveredHandle
              }"
              @mouseenter="handleMouseEnter(tier1)"
            >
              <a
                class="site-header__navigation-link text-heading-2xs"
                :style="{
                  color: getTierSpecialColor(tier1),
                }"
                :href="tier1.url"
                v-text="tier1.title"
              />
            </li>
          </ul>
        </nav>
      </div>

      <template
        v-for="(tier1) of desktop.items"
        :key="tier1.handle"
      >
        <navigation-tiers
          v-show="hoveredHandle === tier1.handle && tierHasChildren(tier1)"
          :tier="tier1"
          @mouseleave="handleMouseLeave"
        />
      </template>
    </header>

    <!-- Predictive search -->
    <transition name="fade">
      <div
        v-if="search.active"
        class="predictive-search-overlay site-header__predictive-search"
      >
        <predictive-search
          v-if="getComponentVisible('predictive-search')"
          v-cloak
          :is-active="search.active"
          :query="search.query"
          :search-bars="[$refs.searchBarMobile.$el, $refs.searchBarDesktop.$el]"
          :search-settings="searchSettings"
          @close-search="search.active = false"
          @update-query="(query) => search.query = query"
          @update-redirect="(redirect) => search.redirect = redirect"
        />

        <predictive-search-loading
          v-if="!getComponentActive('predictive-search')"
          class="predictive-search container grid"
        />
      </div>
    </transition>

    <overlay
      key="predictiveSearch"
      direction="up"
      namespace="predictiveSearch"
      no-content
      type="modal"
    />

    <!-- Navigation tiers -->
    <overlay
      key="navigationTiers"
      direction="up"
      namespace="navigationTiers"
      no-content
      type="modal"
    />

    <!-- Menu drawer -->
    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menuDrawer"
      :template="false"
      type="drawer"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :handle="mobile.handle"
          namespace="menuDrawer"
          :is-active="isActive"
          :item-count="mobile.itemCount"
        />

        <div
          v-if="!getComponentActive('menu-drawer')"
          class="site-header__menu-drawer"
        >
          <div
            v-for="(_, index) of [...Array(mobile.itemCount)]"
            :key="`menu-drawer-loading-${index}`"
            class="site-header__menu-drawer-link"
          >
            <span
              class="site-header__menu-drawer-loading"
              :style="{ animationDelay: $timing('quick', { index, unit: 's' }) }"
            />
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} announcementSettings - Announcement banner settings.
 * @param {Array} announcements - Announcement banner items
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} misc - Desktop misc linklist handle and item count.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {Object} searchSettings - Predictive search settings.
 * @param {Boolean} showWishlist - Show wishlist icon.
 * @param {String} titleElement - Title HTML element.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import AnnouncementBanner from '~global/announcement-banner/announcement-banner'
import NavigationTiers from '~async/navigation-tiers/navigation-tiers'
import Overlay from '~global/overlay/overlay'
import PredictiveSearchLoading from '~async/predictive-search/predictive-search-loading'
import SearchBar from '~global/search-bar/search-bar'

import IconAccount from '~icons/misc/account.svg'
import IconCart from '~icons/misc/cart.svg'
import IconChildsplay from '~icons/misc/childsplay.svg'
import IconWishlist from '~icons/misc/heart.svg'

import { validateNavigationMenuTitle } from '~/helpers/vue'

export default {
  name: 'SiteHeader',

  components: {
    AnnouncementBanner,
    IconAccount,
    IconCart,
    IconChildsplay,
    IconWishlist,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    NavigationTiers,
    Overlay,
    PredictiveSearch: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/predictive-search/predictive-search'
      ),
    }),
    PredictiveSearchLoading,
    SearchBar,
  },

  props: {
    announcementSettings: {
      type: Object,
      default: () => ({
        backgroundColour: '#000000',
        enabled: true,
        speed: 0,
        textColour: '#ffffff',
      }),
    },
    announcements: {
      type: Array,
      default: () => [],
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    misc: {
      type: Object,
      default: () => ({
        handle: 'misc-menu',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    searchSettings: {
      type: Object,
      default: () => ({
        popularSearches: [],
        trendingProducts: [],
      }),
    },
    showWishlist: {
      type: Boolean,
      default: true,
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
  },

  data() {
    return {
      height: {
        total: 0,
        visible: 0,
      },
      hoveredHandle: false,
      navigationTiersOverlay: false,
      search: {
        active: false,
        query: '',
        redirect: '',
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
    }),
  },

  watch: {

    /**
     * Watch active overlay to disable search/navigation tiers when it closes.
     */
    getActiveOverlay() {
      if (this.getActiveOverlay !== 'predictiveSearch') {
        this.search.active = false
        this.search.query = ''
      }

      if (this.getActiveOverlay !== 'navigationTiers') {
        this.navigationTiersOverlay = false
      }
    },

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeaderContainer.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'translate') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible'() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch isMobile to update heights.
     */
    isMobile() {
      this.setHeights(true)
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },

    /**
     * Watch navigation tiers overlay state to handle open/close modal.
     */
    navigationTiersOverlay() {
      if (this.navigationTiersOverlay) {
        this.openOverlay({
          component: 'navigation-tiers',
          ignoreDismissed: true,
          namespace: 'navigationTiers',
        })

        return
      }

      this.closeOverlay('navigationTiers')
    },

    /**
     * Watch search active to open/close associated overlay.
     */
    'search.active'() {
      if (this.search.active) {
        this.navigationTiersOverlay = false
        this.hoveredHandle = false

        this.openOverlay({
          component: 'predictive-search',
          ignoreDismissed: true,
          namespace: 'predictiveSearch',
        })

        return
      }

      this.closeOverlay('predictiveSearch')
    },
  },

  mounted() {
    nextTick().then(() => {
      this.setHeights(true)
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      setStart: 'setStart',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Set heights of fixed header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * - Remove setHeights if header is not fixed.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      /**
       * Visible height is element height combined with translateY value.
       */
      this.height.visible =
        this.$refs.siteHeader.offsetHeight +
        this.$refs.siteHeader.getBoundingClientRect()?.y

      /**
       * Update scroll start height.
       * - This is the point at which the site header can collapse.
       */
      this.setStart(this.height.visible)
    },

    /**
     * Handle container click.
     * - Closes predictive search on mobile.
     */
    handleContainerClick() {
      if (!this.isMobile) {
        return
      }

      this.search.active = false
    },

    /**
     * Handle search focus.
     * - Prevents predicitve search opening on search page.
     * @param {Boolean} active - Search is active?
     */
    handleSearchFocus(active) {
      this.search.active = active
    },

    /**
     * Check if the tier has a color inside its links.
     * @param {object} tier
     * @return {string}
     */
    getTierSpecialColor(tier) {
      return tier.links.find((item) => {
        return item.title.includes('[color]')
      })?.title?.replace('[color]', '')
    },

    /**
     * Sets the hovered handle.
     * @param {object} tier
     */
    handleMouseEnter(tier) {
      if (this.search.active) {
        return
      }

      this.hoveredHandle = tier.handle

      if (tier.links.length && this.tierHasChildren(tier)) {
        this.navigationTiersOverlay = true

        return
      }

      /**
       * If the tier has no children links, we need to close overlay.
       */
      this.navigationTiersOverlay = false
    },

    /**
     * Sets the hovered handle.
     */
    handleMouseLeave() {
      this.hoveredHandle = false
      this.navigationTiersOverlay = false
    },

    /**
     * Checks if the tier has non-meta child link.
     * @param {object} tier
     */
    tierHasChildren(tier) {
      return tier.links?.some(
        (child) => !validateNavigationMenuTitle(child.title),
      )
    },
  },
}
</script>
